import { useEffect, useState } from 'react';
import { InviteCard, SupportCard } from '../features/engage/EngageCards';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '@/api/client';
import { AppShell, LanguageToggle } from '@/components/layout';
import { Alert, Button, Card, Chip, Field, Input, OtpInput, Sheet } from '@/components/ui';
import { useSession } from '@/hooks/session';
import { useLocale, useT } from '@/i18n';
import { countryName } from '@/lib/country';
import { formatDate, formatMoney } from '@/lib/format';
import { REMINDER_HOURS, type Channel, type Subscription } from '@/api/types';
import { offersWhatsApp, offersWhatsAppReminders } from '@/features/auth/channels';
import { DeliveryChoice, deliveryQuestionShown } from '@/features/messaging/DeliveryChoice';
import { FamilySettings } from '@/features/family/FamilySettings';
import { PrivacyCentre } from '@/features/privacy/PrivacyCentre';

export const Settings = () => {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { account, config, refresh, signOut } = useSession();

  const [name, setName] = useState(account?.name ?? '');
  const [marketingOptIn, setMarketingOptIn] = useState(account?.marketingOptIn ?? false);
  const [remindOn, setRemindOn] = useState(account?.reminders?.whatsapp ?? false);
  const [remindHour, setRemindHour] = useState<number>(account?.reminders?.hour ?? 18);

  const [channelIdentifier, setChannelIdentifier] = useState('');
  const [channelCode, setChannelCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const [password, setPassword] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteDataOpen, setDeleteDataOpen] = useState(false);

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const plusPlan = config?.plans.plus;

  // Fetch the account's own Nurture Plus subscription once there's a signed-in account. Needed even
  // where the plan is no longer offered: deleting data or the account must warn about live autopay.
  useEffect(() => {
    if (!account) return undefined;
    let cancelled = false;
    void api.subscriptions
      .current()
      .then(({ subscription: sub }) => {
        if (!cancelled) setSubscription(sub ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [account]);

  if (!account) return null;

  const planActive = subscription?.status === 'active';
  // Mirrors the API's rule (domain/privacy.ts): a plan that will still charge the card.
  const autopayOn =
    !!subscription && ['authenticated', 'active', 'pending'].includes(subscription.status) && !subscription.cancelAtPeriodEnd;
  const renewsOn = formatDate(subscription?.currentPeriodEnd ?? '');

  const missingChannel: Extract<Channel, 'email' | 'whatsapp'> = account.email ? 'whatsapp' : 'email';
  const whatsappOffered = offersWhatsApp(config);
  const canAddChannel = missingChannel === 'email' || whatsappOffered;

  const run = async (key: string, work: () => Promise<string>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      setNotice(await work());
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(null);
    }
  };

  const handleDeleteData = () =>
    run('delete-data', async () => {
      await api.me.removeData();
      setDeleteDataOpen(false);
      return t('settings.deleteDataDone');
    });

  const cancelAutopay = () =>
    run('cancel', async () => {
      const { subscription: updated } = await api.subscriptions.cancel(subscription!.subscriptionId);
      setSubscription(updated);
      return t('settings.autopay.off', { date: formatDate(updated.currentPeriodEnd ?? '') });
    });

  /** Shown inside both delete sheets while Nurture Plus would still charge the card. */
  const autopayNotice = (body: string) =>
    autopayOn && (
      <div role="alert" className="mt-4 rounded-md bg-attention-soft p-4 text-attention-ink">
        <p className="text-label">{t('settings.autopay.title')}</p>
        <p className="mt-1 text-body">{body}</p>
        <Button variant="secondary" className="mt-3" loading={busy === 'cancel'} onClick={() => void cancelAutopay()}>
          {t('settings.autopay.cancel')}
        </Button>
      </div>
    );

  const handleDelete = async () => {
    setBusy('delete');
    setError(null);
    try {
      await api.me.remove();
      signOut();
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setBusy(null);
    }
  };

  return (
    <AppShell>
      <h1 className="mb-5 text-h1 text-ink">{t('settings.title')}</h1>

      {notice && (
        <div className="mb-4">
          <Alert>{notice}</Alert>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <Alert tone="warn">{error}</Alert>
        </div>
      )}

      <Card className="mb-4 space-y-4">
        <h2 className="text-h3 text-ink">{t('settings.profile')}</h2>
        <Field id="settings-name" label={t('settings.name')}>
          <Input value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} />
        </Field>
        <dl className="text-caption text-ink-muted">
          <div className="flex justify-between">
            <dt>{t('settings.country')}</dt>
            <dd>{countryName(account.country, locale)}</dd>
          </div>
        </dl>
        <Chip selected={marketingOptIn} onToggle={setMarketingOptIn}>
          {t('settings.marketing')}
        </Chip>
        <Button
          loading={busy === 'profile'}
          onClick={() =>
            void run('profile', async () => {
              await api.me.update({ name, marketingOptIn });
              return t('settings.saved');
            })
          }
        >
          {t('common.save')}
        </Button>
      </Card>

      {deliveryQuestionShown(account, config) && (
        <Card className="mb-4">
          <DeliveryChoice source="settings" />
        </Card>
      )}

      {account.whatsappOptOut && whatsappOffered && (
        <Card className="mb-4 space-y-3">
          <p className="text-body text-ink">{t('settings.whatsappStopped')}</p>
          <Button
            variant="secondary"
            loading={busy === 'whatsappResume'}
            onClick={() =>
              void run('whatsappResume', async () => {
                await api.me.resumeWhatsApp();
                await refresh();
                return t('settings.whatsappResumed');
              })
            }
          >
            {t('settings.whatsappResume')}
          </Button>
        </Card>
      )}

      {offersWhatsAppReminders(config) && (
        <Card className="mb-4 space-y-4">
          <h2 className="text-h3 text-ink">{t('settings.reminders')}</h2>
          <p className="text-body text-ink-muted">{t('settings.reminders.body')}</p>
          {account.phone && account.phoneVerified ? (
            <>
              <Chip selected={remindOn} onToggle={setRemindOn}>
                {t('settings.reminders.on')}
              </Chip>
              {remindOn && (
                <div className="flex flex-wrap gap-2" role="group" aria-label={t('settings.reminders.time')}>
                  {REMINDER_HOURS.map((h) => (
                    <Chip key={h} selected={remindHour === h} onToggle={() => setRemindHour(h)}>
                      {t('settings.reminders.hour', { time: `${h % 12 || 12}${h < 12 ? ' am' : ' pm'}` })}
                    </Chip>
                  ))}
                </div>
              )}
              <Button
                loading={busy === 'reminders'}
                onClick={() =>
                  void run('reminders', async () => {
                    await api.me.update({ reminders: { whatsapp: remindOn, hour: remindHour } });
                    return t('settings.saved');
                  })
                }
              >
                {t('settings.reminders.save')}
              </Button>
            </>
          ) : (
            <p className="text-caption text-ink-muted">{t('settings.reminders.needPhone')}</p>
          )}
        </Card>
      )}

      {plusPlan && (
        <Card className="mb-4 space-y-3">
          <h2 className="text-h3 text-ink">{t('settings.plan')}</h2>
          {planActive && subscription ? (
            <>
              <p className="text-body text-ink">{t('plan.plus.title')}</p>
              <p className="text-caption text-ink-muted">
                {subscription.cancelAtPeriodEnd
                  ? t('settings.plan.ends', { date: formatDate(subscription.currentPeriodEnd ?? '') })
                  : t('settings.plan.renews', { date: formatDate(subscription.currentPeriodEnd ?? '') })}
              </p>
              <p className="text-label text-ink">
                {t('plan.plus.remaining', { n: subscription.quota.remaining, total: subscription.quota.perMonth })}
              </p>
              {!subscription.cancelAtPeriodEnd && (
                <Button variant="secondary" onClick={() => setCancelOpen(true)}>
                  {t('settings.plan.cancel')}
                </Button>
              )}
            </>
          ) : (
            <>
              <p className="text-body text-ink-soft">
                {t('plan.plus.body', { n: plusPlan.unlocksPerMonth, price: formatMoney(plusPlan.amount, plusPlan.currency) })}
              </p>
              <Button onClick={() => navigate('/plus')}>{t('plan.plus.cta')}</Button>
            </>
          )}
        </Card>
      )}

      <Card className="mb-4 space-y-3">
        <h2 className="text-h3 text-ink">{t('settings.language')}</h2>
        <LanguageToggle />
      </Card>

      {canAddChannel && (
        <Card className="mb-4 space-y-4">
          <h2 className="text-h3 text-ink">{t('settings.channels')}</h2>
          <div>
            <p className="text-label text-ink">{missingChannel === 'email' ? t('settings.addEmail') : t('settings.addWhatsapp')}</p>
            <p className="text-caption text-ink-muted">{t('settings.channelBody')}</p>
          </div>

          <Field id="settings-channel-identifier" label={missingChannel === 'email' ? t('signin.email') : t('signin.phone')}>
            <Input
              type={missingChannel === 'email' ? 'email' : 'tel'}
              value={channelIdentifier}
              onChange={(e) => setChannelIdentifier(e.target.value)}
            />
          </Field>

          {codeSent && <OtpInput label={t('otp.label')} value={channelCode} onChange={setChannelCode} />}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              loading={busy === 'send'}
              disabled={!channelIdentifier}
              onClick={() =>
                void run('send', async () => {
                  const res = await api.auth.startOtp({ channel: missingChannel, identifier: channelIdentifier, purpose: 'add_channel' });
                  setCodeSent(true);
                  return res.devCode ? t('signin.devCode', { code: res.devCode }) : t('settings.codeSent');
                })
              }
            >
              {t('settings.sendCode')}
            </Button>
            {codeSent && (
              <Button
                loading={busy === 'verify'}
                disabled={channelCode.length !== 6}
                onClick={() =>
                  void run('verify', async () => {
                    await api.auth.addChannel({ channel: missingChannel, identifier: channelIdentifier, code: channelCode });
                    setCodeSent(false);
                    setChannelCode('');
                    return t('settings.channelAdded');
                  })
                }
              >
                {t('settings.verifyCode')}
              </Button>
            )}
          </div>
        </Card>
      )}

      <Card className="mb-4 space-y-4">
        <h2 className="text-h3 text-ink">{account.hasPassword ? t('settings.changePassword') : t('settings.setPassword')}</h2>
        <Field id="settings-password" label={t('settings.newPassword')} hint={t('settings.passwordHint')}>
          <Input
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button
          loading={busy === 'password'}
          disabled={password.length < 8}
          onClick={() =>
            void run('password', async () => {
              await api.auth.setPassword(password);
              setPassword('');
              return t('settings.passwordSaved');
            })
          }
        >
          {t('settings.savePassword')}
        </Button>
      </Card>

      <FamilySettings accountName={account.name ?? ''} />

      <InviteCard />

      <SupportCard />

      <PrivacyCentre />

      <Card id="your-data" className="mb-4 scroll-mt-24 space-y-3">
        <h2 className="text-h3 text-ink">{t('settings.export')}</h2>
        <Button
          variant="secondary"
          loading={busy === 'export'}
          onClick={() =>
            void run('export', async () => {
              const data = await api.me.exportData();
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'nurture-my-data.json';
              link.click();
              URL.revokeObjectURL(url);
              return t('settings.exportDone');
            })
          }
        >
          {t('settings.export')}
        </Button>
      </Card>

      <Card className="space-y-4">
        <div>
          <h2 className="text-h3">{t('settings.data.title')}</h2>
          <p className="mt-1 text-body text-ink-soft">{t('settings.data.body')}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-md bg-paper p-4">
            <h3 className="text-label text-ink">{t('settings.deleteData')}</h3>
            <p className="mt-1 text-caption text-ink-muted">{t('settings.deleteDataBody')}</p>
            <Button variant="secondary" className="mt-3" onClick={() => setDeleteDataOpen(true)}>
              {t('settings.deleteData')}
            </Button>
          </div>
          <div className="rounded-md bg-alert-soft p-4">
            <h3 className="text-label text-alert-ink">{t('settings.delete')}</h3>
            <p className="mt-1 text-caption text-alert-ink">{t('settings.deleteBody')}</p>
            <Button variant="danger" className="mt-3" onClick={() => setDeleteOpen(true)}>
              {t('settings.delete')}
            </Button>
          </div>
        </div>
      </Card>

      <Sheet open={deleteDataOpen} onClose={() => setDeleteDataOpen(false)} title={t('settings.deleteData')}>
        <p className="text-body text-ink-soft">{t('settings.deleteDataBody')}</p>
        <p className="mt-2 text-label text-ink">{t('settings.cannotUndo')}</p>
        {autopayNotice(t('settings.autopay.data', { date: renewsOn }))}
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="danger" loading={busy === 'delete-data'} onClick={() => void handleDeleteData()}>
            {t('settings.deleteData')}
          </Button>
          <Button variant="secondary" onClick={() => setDeleteDataOpen(false)}>
            {t('common.cancel')}
          </Button>
        </div>
      </Sheet>

      <Sheet open={deleteOpen} onClose={() => setDeleteOpen(false)} title={t('settings.delete')}>
        <p className="text-body text-ink-soft">{t('settings.deleteBody')}</p>
        <p className="mt-2 text-label text-ink">{t('settings.cannotUndo')}</p>
        {autopayNotice(t('settings.autopay.account'))}
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="danger" disabled={autopayOn} loading={busy === 'delete'} onClick={() => void handleDelete()}>
            {t('settings.deleteConfirm')}
          </Button>
          <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
            {t('common.cancel')}
          </Button>
        </div>
      </Sheet>

      <Sheet open={cancelOpen} onClose={() => setCancelOpen(false)} title={t('settings.plan.cancel')}>
        <p className="text-body text-ink-soft">{t('settings.plan.cancelBody')}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            variant="danger"
            loading={busy === 'cancel'}
            onClick={() =>
              void run('cancel', async () => {
                const { subscription: updated } = await api.subscriptions.cancel(subscription!.subscriptionId);
                setSubscription(updated);
                setCancelOpen(false);
                return t('settings.plan.cancelled', { date: formatDate(updated.currentPeriodEnd ?? '') });
              })
            }
          >
            {t('settings.plan.cancel')}
          </Button>
          <Button variant="secondary" onClick={() => setCancelOpen(false)}>
            {t('common.cancel')}
          </Button>
        </div>
      </Sheet>
    </AppShell>
  );
};
